import { useSyncExternalStore } from 'react'
import type { ClockKind, ClockStatus } from '../../../api/time_clock/requests'

// Punches made without a connection (slide 7). They wait on this device with the device's time and
// are sent in order once it is back online; the server records each at its own (drift-corrected)
// time, or refuses it when the record changed meanwhile. A refused punch stays here, visibly, until
// the employee requests a correction or discards it. Nothing is ever dropped silently.
//
// Location (slide 26: read at clock in/out only, for that check): coordinates wait here only while
// the punch can still be sent. They are removed once it is refused, removed or past the window in
// which the server accepts a queued punch, for every person who used this device.

export interface QueuedPunch {
  request_id: string
  kind: ClockKind
  /** The device's clock when the punch was made (ISO, with offset). */
  device_time: string
  /** Who was signed in, in which organisation, when it was made. It is only ever sent for them. */
  owner_employee: string
  owner_organization: string
  /** Server time minus device time, as last seen before the punch; reveals a clock changed meanwhile. */
  punch_offset_seconds?: number
  latitude?: number
  longitude?: number
  accuracy?: number
  /** Set when the server refused it: the code says why. */
  refused?: string
}

/** The server's own limit for adding a queued punch automatically (its 7-day state window). */
export const MAX_WAIT_MS = 7 * 24 * 60 * 60 * 1000

const PREFIX = 'aboutwork.clock.offline.'
const KEY = (employeeId: string) => `${PREFIX}${employeeId}`
const CHANGED = 'aboutwork-clock-offline'

export function readQueue(employeeId: string): QueuedPunch[] {
  if (!employeeId) return []
  try {
    const value = JSON.parse(localStorage.getItem(KEY(employeeId)) ?? '[]')
    return Array.isArray(value) ? value.filter((item) => item && typeof item.request_id === 'string' && typeof item.device_time === 'string') : []
  } catch {
    return []
  }
}

function writeQueue(employeeId: string, queue: QueuedPunch[]) {
  try {
    if (queue.length) localStorage.setItem(KEY(employeeId), JSON.stringify(queue))
    else localStorage.removeItem(KEY(employeeId))
  } catch {
    // Storage blocked (private window): the punch cannot wait offline, which the caller reports.
    throw new Error('offline_storage_unavailable')
  }
  window.dispatchEvent(new Event(CHANGED))
}

export function enqueuePunch(employeeId: string, punch: QueuedPunch) {
  writeQueue(employeeId, [...readQueue(employeeId), punch])
}

export function updateQueue(employeeId: string, change: (queue: QueuedPunch[]) => QueuedPunch[]) {
  writeQueue(employeeId, change(readQueue(employeeId)))
}

/** The punch without its location. */
export function withoutLocation({ latitude: _latitude, longitude: _longitude, accuracy: _accuracy, ...punch }: QueuedPunch): QueuedPunch {
  return punch
}

/** Refused: kept visible for a correction, its location no longer needed. */
export function refuse(punch: QueuedPunch, code: string): QueuedPunch {
  return withoutLocation({ ...punch, refused: code })
}

/**
 * Applies the waiting limits to everyone's punches on this device: a punch past the server's window is
 * shown as too old (it can only become a correction) and loses its location, as does any refused one.
 */
export function cleanUpOfflinePunches(now = Date.now()) {
  let keys: string[] = []
  try {
    keys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index) ?? '').filter((key) => key.startsWith(PREFIX))
  } catch {
    return
  }
  for (const key of keys) {
    const employeeId = key.slice(PREFIX.length)
    const queue = readQueue(employeeId)
    const cleaned = queue.map((punch) => {
      if (!punch.refused && !(now - Date.parse(punch.device_time) < MAX_WAIT_MS)) return refuse(punch, 'offline_too_old')
      return punch.refused && 'latitude' in punch ? withoutLocation(punch) : punch
    })
    if (cleaned.some((punch, index) => punch !== queue[index])) {
      try {
        writeQueue(employeeId, cleaned)
      } catch {
        // Storage blocked: nothing can be kept here either.
      }
    }
  }
}

const subscribe = (notify: () => void) => {
  window.addEventListener(CHANGED, notify)
  window.addEventListener('storage', notify)
  return () => {
    window.removeEventListener(CHANGED, notify)
    window.removeEventListener('storage', notify)
  }
}

/** The punches waiting on this device for the signed-in employee, kept current across tabs. */
export function useOfflineQueue(employeeId: string): QueuedPunch[] {
  const raw = useSyncExternalStore(subscribe, () => {
    try {
      return (employeeId && localStorage.getItem(KEY(employeeId))) || '[]'
    } catch {
      return '[]'
    }
  })
  return employeeId ? readQueueFrom(raw) : []
}

const parsed = new Map<string, QueuedPunch[]>()
function readQueueFrom(raw: string): QueuedPunch[] {
  if (!parsed.has(raw)) {
    try {
      const value = JSON.parse(raw)
      parsed.clear()
      parsed.set(raw, Array.isArray(value) ? value : [])
    } catch {
      return []
    }
  }
  return parsed.get(raw) ?? []
}

const NEXT: Record<ClockKind, { state: ClockStatus['state']; allowed: ClockKind[] }> = {
  in: { state: 'working', allowed: ['pause_start', 'out'] },
  pause_start: { state: 'paused', allowed: ['pause_end', 'out'] },
  pause_end: { state: 'working', allowed: ['pause_start', 'out'] },
  out: { state: 'off', allowed: ['in'] },
}

/** The clock as the employee left it: the server's status with the punches still waiting applied. */
export function withQueuedPunches(status: ClockStatus, queue: QueuedPunch[]): ClockStatus {
  if (!status.owner) return status
  const waiting = queue.filter((punch) => !punch.refused
    && punch.owner_employee === status.owner?.employee
    && punch.owner_organization === status.owner?.organization)
  if (!waiting.length) return status
  const last = waiting[waiting.length - 1]
  const next = NEXT[last.kind]
  return {
    ...status,
    state: next.state,
    allowed: next.allowed,
    since: last.device_time,
    session_started_at: last.kind === 'in' ? last.device_time : last.kind === 'out' ? null : status.session_started_at,
  }
}

/** A request that never reached the server, as opposed to one the server answered. */
export function isConnectionFailure(error: unknown) {
  const response = (error as { response?: unknown } | null)?.response
  return (typeof navigator !== 'undefined' && navigator.onLine === false) || (!!error && typeof error === 'object' && 'isAxiosError' in error && !response)
}
